import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  AdjustItemMeasurementsInput,
  CreateOrderInput,
  NextOrderNumber,
  OrderItem,
  OrderPage,
  OrderWithItems,
  UpdateOrderItemInput,
  UpdateOrderInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  adjustItemMeasurements,
  cancelOrder,
  cancelOrderItem,
  createOrder,
  getOrder,
  listOrders,
  nextOrderNumber,
  repeatOrder,
  updateOrder,
  updateOrderItem,
  type OrderListParams,
} from './orders-api.js';

export const orderKeys = {
  all: ['orders'] as const,
  list: (params: OrderListParams) => ['orders', 'list', params] as const,
  detail: (orderId: string) => ['orders', 'detail', orderId] as const,
  nextNumber: () => ['orders', 'next-number'] as const,
};

/**
 * `enabled` exists for the callers that may not have a customer yet — asking the order book for
 * `customerId: undefined` would fetch the whole shop's first page to throw it away.
 */
export function useOrders(
  params: OrderListParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<OrderPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: orderKeys.list(params),
    queryFn: () => listOrders(client, params),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useOrder(orderId: string | null): UseQueryResult<OrderWithItems> {
  const client = useApiClient();
  return useQuery({
    queryKey: orderKeys.detail(orderId ?? ''),
    queryFn: () => getOrder(client, orderId as string),
    enabled: orderId !== null,
  });
}

/**
 * Every order mutation invalidates the whole order namespace rather than patching the cache.
 * A price change moves the totals, which moves the unpaid filter, which moves the tab counts —
 * three derived numbers a hand-patched cache would get subtly wrong.
 */
function useOrderMutation<TVariables, TData = OrderWithItems>(
  run: (client: ReturnType<typeof useApiClient>, variables: TVariables) => Promise<TData>,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationFn: (variables) => run(client, variables),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orderKeys.all });
      // Booking moves the customer's order count and last-order date.
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

/**
 * The number the booking about to be typed will most likely take.
 *
 * Never cached across a visit — `staleTime: 0` and no placeholder — because the shop's other
 * counter may have booked in between, and a stale number is worse than no number: it is the
 * one the customer has already been told. Failure is silent on purpose; the screen simply does
 * not show a number, and the booking itself does not depend on this call in any way.
 */
export function useNextOrderNumber(
  options: { enabled?: boolean } = {},
): UseQueryResult<NextOrderNumber> {
  const client = useApiClient();
  return useQuery({
    queryKey: orderKeys.nextNumber(),
    queryFn: () => nextOrderNumber(client),
    enabled: options.enabled ?? true,
    staleTime: 0,
    retry: false,
  });
}

export function useCreateOrder() {
  return useOrderMutation<CreateOrderInput>((client, input) => createOrder(client, input));
}

export function useUpdateOrder(orderId: string) {
  return useOrderMutation<UpdateOrderInput>((client, patch) => updateOrder(client, orderId, patch));
}

export function useUpdateOrderItem(orderId: string) {
  return useOrderMutation<{ itemId: string; patch: UpdateOrderItemInput }>((client, variables) =>
    updateOrderItem(client, orderId, variables.itemId, variables.patch),
  );
}

export function useAdjustItemMeasurements(orderId: string) {
  return useOrderMutation<{ itemId: string; input: AdjustItemMeasurementsInput }, OrderItem>(
    (client, variables) =>
      adjustItemMeasurements(client, orderId, variables.itemId, variables.input),
  );
}

export function useCancelOrder(orderId: string) {
  return useOrderMutation<string>((client, reason) => cancelOrder(client, orderId, reason));
}

export function useCancelOrderItem(orderId: string) {
  return useOrderMutation<{ itemId: string; reason: string }>((client, variables) =>
    cancelOrderItem(client, orderId, variables.itemId, variables.reason),
  );
}

export function useRepeatOrder(orderId: string) {
  return useOrderMutation<string | null>((client, promisedDate) =>
    repeatOrder(client, orderId, promisedDate),
  );
}
